import classNames from "classnames";
import Button, { buildHidePopoverIfAlreadyShowing, ButtonWithPopover, defaultAnimationWithHidingSmooth } from "Components/button";
import Close from "Components/close";
import Menu from "Components/menu";
import ScrolledDropdown from "Components/scrolledDropdown";
import ScrollView from "devextreme-react/scroll-view";
import filter from "lodash/filter";
import includes from "lodash/includes";
import map from "lodash/map";
import pick from "lodash/pick";
import reject from "lodash/reject";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import buildExportCallback from "../utils/buildExportCallback";
import locale from "./documentObjectTypesPanel.locale";
import Styled from "../rowActionsPanel/rowActionsPanel.styled";
const keySelector = (e) => e.Id;
const textSelector = (e) => e.Name;
const emptyList = [];
const SIZES = {
    LIST_WIDTH: 270,
    LIST_HEIGHT: 180,
    MAIN_BUTTON_HEIGHT: 24,
    BLOCK_HEIGHT: 22,
    POPUP_PANEL_BUTTONS_GAP: 15
};
const menuPosition = {
    my: "top left",
    at: "bottom left",
    collision: "fit flip",
    offset: { y: 2 }
};
const gapStyle = { gap: "0.25em" };
const blockStyle = { height: SIZES.BLOCK_HEIGHT };
function DocumentObjectTypesPanel(props) {
    const popoverRef = useRef(null);
    const menuRef = useRef();
    const { width, ref: observableRef } = useResizeDetector();
    const [selectedDocumentObjectIds, setSelectedDocumentObjectIds] = useState(props.selectedDocumentObjectIds);
    const handleHideIfAlreadyShowing = useCallback(buildHidePopoverIfAlreadyShowing(popoverRef), []);
    useEffect(() => {
        setSelectedDocumentObjectIds(props.selectedDocumentObjectIds);
    }, [props.selectedDocumentObjectIds]);
    const items = useMemo(() => props.availableDocumentObjectIds && props.documentObjects
        ? map(pick(props.documentObjects, props.availableDocumentObjectIds))
        : undefined, [props.documentObjects, props.availableDocumentObjectIds]);
    const selectedItems = useMemo(() => items && props.selectedDocumentObjectIds
        ? filter(items, item => includes(props.selectedDocumentObjectIds, item.Id))
        : undefined, [items, props.selectedDocumentObjectIds]);
    const handleGetTableData = useCallback(() => {
        var _a;
        if (!selectedDocumentObjectIds)
            return;
        props.onGetTableData && props.onGetTableData(selectedDocumentObjectIds);
        (_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.instance.hide();
    }, [props.onGetTableData, props.onSelectDocumentObjects, selectedDocumentObjectIds]);
    const handleHidden = useCallback(() => {
        var _a;
        ((_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.setSearchText) && menuRef.current.setSearchText("");
        setSelectedDocumentObjectIds(props.selectedDocumentObjectIds);
    }, [props.selectedDocumentObjectIds]);
    const popoverContent = useMemo(() => (React.createElement("div", null,
        React.createElement("div", null,
            React.createElement(ScrolledDropdown, { keySelector: keySelector, textSelector: textSelector, selectedKey: selectedDocumentObjectIds, multi: true, togglerComponent: null, itemClassName: "pl-0_5", menuRef: menuRef, showSearchInputWhenItemsLengthIsGreaterThan: -1, onSelectedMultiChanged: setSelectedDocumentObjectIds, items: items || emptyList, maxHeight: SIZES.LIST_HEIGHT, minHeight: SIZES.LIST_HEIGHT, height: SIZES.LIST_HEIGHT, minWidth: SIZES.LIST_WIDTH, maxWidth: SIZES.LIST_WIDTH, emptyMessage: locale.NO_AVAILABLE_OBJECT_TYPES })),
        React.createElement("div", { className: "mt-0_25 flex-space-between p-0_5" },
            React.createElement(Button, { className: "btn-primary", minWidth: SIZES.LIST_WIDTH / 2 - SIZES.POPUP_PANEL_BUTTONS_GAP, disabled: !selectedDocumentObjectIds, onClick: handleGetTableData }, locale.OK),
            React.createElement(Button, { minWidth: SIZES.LIST_WIDTH / 2 - SIZES.POPUP_PANEL_BUTTONS_GAP, onClick: handleHideIfAlreadyShowing }, locale.CANCEL)))), [
        items,
        selectedDocumentObjectIds,
        handleHideIfAlreadyShowing,
        handleGetTableData,
        props.onSelectDocumentObjects
    ]);
    const containerStyle = useMemo(() => ({ height: props.panelHeight, ...gapStyle }), [props.panelHeight]);
    const exportMenuItems = useMemo(() => [
        {
            text: locale.IMPORT_TO_XLSX,
            onClick: buildExportCallback(props.gridRef, "xlsx", props.documentName)
        }
    ], [Boolean(props.gridRef), props.documentName]);
    return (React.createElement("div", { className: classNames("pr-0_75 grid-cols-1fr-auto w-100-max v-center-items bg-white", props.className), style: containerStyle },
        React.createElement("div", { className: "grid-cols-auto-1fr v-center-items" },
            React.createElement("div", { className: "grid-cols-auto no-gap" },
                React.createElement(Styled.BtnContainer, null,
                    React.createElement(ButtonWithPopover, { className: "no-border pl-1 pr-1 pt-0_5 pb-0_5 font-small btn-filled", buttonHeight: SIZES.MAIN_BUTTON_HEIGHT, showEvent: "dxclick", animation: defaultAnimationWithHidingSmooth, position: menuPosition, popoverRef: popoverRef, onClick: handleHideIfAlreadyShowing, onHidden: handleHidden, ignoreHidingWhenChanged: true, popoverContent: popoverContent, popoverClassName: "p-xxs", disabled: props.disabled },
                        React.createElement("i", { className: classNames("icon-add font-h5 ml-0_25 mr-0_25") }),
                        locale.TABLE_SETTINGS)),
                React.createElement("div", { className: "vertical-divider ml-0_5 mr-0_75" })),
            React.createElement("div", { className: "overflow-hidden", ref: observableRef },
                React.createElement(ScrollView, { height: props.panelHeight, width: width, showScrollbar: "onHover", direction: "horizontal" },
                    React.createElement("div", { className: "grid-cols-auto w-max-content", style: containerStyle }, map(selectedItems, item => (React.createElement("div", { key: item.Id, style: blockStyle, className: "bg-light-primary-fixed border-rounded-medium caption-with-actions-panel v-center-items with-gap-xxs pl-0_25 pr-0_25" },
                        React.createElement("div", { className: "nowrap font-small" }, item.Name),
                        React.createElement(Close, { fontSize: 10, size: 16, className: "text-light-on-surface-variant", animateHoverColor: true, onClick: () => {
                                props.onGetTableData &&
                                    props.onGetTableData(reject(map(selectedItems, "Id"), e => e === item.Id));
                            } })))))))),
        props.gridRef && (React.createElement(Styled.BtnContainer, null,
            React.createElement(Menu, { togglerClassName: "font-small btn-filled", togglerIconClassName: "dx-icon dxre-icon-Download font-h6 pr-0_5", togglerText: locale.EXPORT, togglerHeight: SIZES.MAIN_BUTTON_HEIGHT, togglerWidth: "auto", items: exportMenuItems })))));
}
export default memo(DocumentObjectTypesPanel);
